import { Injectable, NgZone, OnDestroy } from '@angular/core'
import { NavigationEnd, NavigationStart, Router } from '@angular/router'
import posthog from 'posthog-js'
import { BehaviorSubject, combineLatest, filter, of, Subscription, take, timeout } from 'rxjs'
import { ParseService } from 'src/app/parse.service'
import { Objects } from '../../../../../server/src/shared-with-client/object-definitions'
import { EmptyObject, UnknownObject } from '../../../../../server/src/shared-with-client/types'
import { EnvironmentService } from '../../environment.service'
import { SearchBy } from '../../mailing-list/purchase/search-by/search-by.component'

export namespace PosthogTracking {
    export namespace Events {
        export type MegaMenuShopSubscriptionNowClicked = 'Mega Menu Shop Subscription Now Clicked'
        export type NavbarCreateNewOrderClicked = 'Navbar Create New Order Clicked'
        export type NavbarSubscribeAndSaveClicked = 'Navbar Subscribe And Save Clicked'

        export type UserPilotBannerClicked = 'User Pilot Banner Clicked'
        export type FreePostcardsCopiedReferralCode = 'Free Postcards Copied Referral Code'
        export type FreePostcardsShowEmailTemplateClicked = 'Free Postcards Show Email Template Clicked'
        export type SuppressionListAdded = 'Suppression List Added'
        export type SuppressionListRowAdded = 'Suppression List Row Added'
        export type SuppressionListRowDeleted = 'Suppression List Row Deleted'
        export type SuppressionListReplaced = 'Suppression List Replaced'
        export type OrderManagementCreateSimilarClicked = 'Order Management Create Similar Clicked'

        export type OrderSummaryPurchaseMailingListClicked = 'Order Summary Purchase Mailing List Clicked'
        export type OrderSummarySaveChangesClicked = 'Order Summary Save Changes Clicked'
        export type OrderSummaryDropOff = 'Order Summary DropOff'
        export type OrderSummaryBackClicked = 'Order Summary Back Clicked'
        export type OrderSummaryCheckoutCompleted = 'Order Summary Checkout Completed'

        export type BillingSubscriptionSliderSlid = 'Billing Subscription Slider Slid'
        export type BillingSubscriptionDroppedOff = 'Billing Subscription Dropped Off'
        export type BillingSubscriptionFiveCentsRemoved = 'Billing Subscription Five Cents Removed'
        export type BillingSubscriptionFiveCentsAdded = 'Billing Subscription Five Cents Added'
        export type BillingSubscriptionPurchased = 'Billing Subscription Purchased'

        export type UploadListUpdateExistingList = 'Upload List Update Existing List'
        export type UploadListAutoUploaded = 'Upload List Auto Uploaded'
        export type UploadListAddedUnmappedFields = 'Upload List Added Unmapped Fields'
        export type UploadListIgnoredUnmappedFields = 'Upload List Ignored Unmapped Fields'
        export type UploadListDeleted = 'Upload List Deleted'
        export type UploadListSaved = 'Upload List Saved'

        export type ManageMailingListUploadClicked = 'Manage Mailing List Upload Clicked'
        export type ManageMailingListPurchaseClicked = 'Manage Mailing List Purchase Clicked'
        export type ManageMailingListAddMyAddressClicked = 'Manage Mailing List Add My Address Clicked'
        export type ManageMailingListAddAddressClicked = 'Manage Mailing List Add Address Clicked'
        export type ManageMailingListRefreshClicked = 'Manage Mailing List Refresh Clicked'
        export type ManageMailingListViewMappedCSVClicked = 'Manage Mailing View Mapped CSV Clicked'
        export type ManageMailingListViewOriginalCSVClicked = 'Manage Mailing Viewed Original CSV Clicked'
        export type ManageMailingListRunAgainstSuppressionListClicked = 'Manage Mailing List Run Against Suppression List Clicked'
        export type ManageMailingListRunDuplicateDetectionClicked = 'Manage Mailing List Run Duplicate Detection Clicked'
        export type ManageMailingListCreateFacebookAudienceClicked = 'Manage Mailing List Create Facebook Audience Clicked'

        export type MailingListProSubscriptionPurchased = 'Mailing List Pro Subscription Purchased'
        export type MailingListPurchased = 'Mailing List Purchased'
        export type MailingListOptionsSelected = 'Mailing List Options Selected'
        export type MailingListProSubscriptionLinkClicked = 'Mailing List Pro Subscription Link Clicked'

        export type AdvancedEditorLoadFailed = 'Advanced Editor Load Failed'
        export type AdvancedEditorLoadCompleted = 'Advanced Editor Load Completed'
        export type AdvancedEditorClicked = 'Advanced Editor Clicked'
        export type AdvancedEditorPreviewDownloaded = 'Advanced Editor Preview Downloaded'
        export type AdvancedEditorSaved = 'Advanced Editor Saved'
        export type AdvancedEditorExited = 'Advanced Editor Exited'
        export type AdvancedEditorSavedAndExited = 'Advanced Editor Saved And Exited'

        export type ExpressEditorSavedAsNewTemplate = 'Express Editor Saved As New Template'
        export type ExpressEditorProofDownloaded = 'Express Editor Proof Downloaded'
        export type ExpressEditorEmailProofSent = 'Express Editor Email Proof Sent'
        export type ExpressEditorNextClicked = 'Express Editor Next Clicked'
        export type ExpressEditorSavedChanges = 'Express Editor Saved Changes'

        export type TemplateViewed = 'Template Viewed'
        export type TemplateProductFilters = 'Templates Filtered'
        export type TemplatesFiltersSelected = 'Templates Filters Selected'
        export type TemplateSearch = 'Templates Searched'
        export type ProductSelected = 'Product Selected'

        export type OnboardingLetsGetStartedClicked = "Onboarding Let's Get Started Clicked"
        export type OnboardingRealEstateAgentNextClicked = 'Real Estate Agent Onboarding Next Clicked'
        export type OnboardingSuccessPageUpgradeNowClicked = 'Onboarding Success Page Upgrade Now Clicked'
        export type OnboardingSuccessPageUpgradeLaterClicked = 'Onboarding Success Page Upgrade Later Clicked'
        export type OnboardingConnectMlsUser = 'Onboarding Mls User Selected'
        export type SkipOnboardingConnectMlsUser = 'Skip Onboarding Connect Mls User Clicked'
        export type OnboardingGetPostcardSampleClicked = 'Onboarding Get Free Postcard Sample Clicked'
        export type OnboardingSkipPostcardSampleClicked = 'Onboarding Skip Free Postcard Sample Clicked'
        export type OnboardingConfirmDesignProfileClicked = 'Onboarding Confirm Design Profile Details Clicked'
        export type OnboardingConfirmDesignProfileBackButtonClicked = 'Onboarding Confirm Design Profile Details Back Button Clicked'
        export type OnboardingDroppedOff = 'Onboarding Dropped Off'
        export type TemplatePageLeftPanelProductSelected = 'Templates Page Left Panel Product Selected'
    }
}

@Injectable({
    providedIn: 'root',
})
export class PosthogService implements OnDestroy {
    private isInitialized$ = new BehaviorSubject(false)
    private pageEnterTime: number = 0
    private previousUrl: string = ''
    private subscriptions = new Subscription()
    private isRecordingSession = false
    private flagsLoaded$ = new BehaviorSubject(false)
    private featureFlags = new BehaviorSubject<Record<string, boolean>>({})
    private multiVariantFlags = new BehaviorSubject<Record<string, string>>({})

    private recordingUrls: RegExp[] = [
        /^\/editor\/[A-Za-z0-9]{10}$/, // Match editor URLs
        /^\/(signup\/thank-you|signup|login)(?:\?|$)/,
        // Add more URL patterns as needed
    ]
    constructor(
        private environmentService: EnvironmentService,
        private router: Router,
        private ngZone: NgZone,
        private parseService: ParseService
    ) {
        this.initPostHog()
        // beforeunload listener
        window.addEventListener('beforeunload', () => {
            if (this.previousUrl) {
                this.trackPageDropoff(this.previousUrl, '')
            }
        })
        // subscribe for user change
        this.handleIdentifyUser()
        posthog.onFeatureFlags((flags: string[]) => {
            this.loadFeatureFlags(flags)
        })

        this.subscriptions.add(
            combineLatest([
                this.router.events.pipe(filter((ev) => ev instanceof NavigationStart || ev instanceof NavigationEnd)),
                this.isInitialized$.pipe(
                    filter((isInitialized) => isInitialized),
                    take(1)
                ),
            ]).subscribe(([ev]) => {
                if (ev instanceof NavigationEnd) {
                    this.pageEnterTime = Date.now()
                    posthog.capture('$pageview')

                    // Handle recording based on URL
                    this.handleSessionRecording(ev.url)

                    this.previousUrl = ev.url
                } else if (ev instanceof NavigationStart) {
                    this.trackPageDropoff(this.previousUrl, ev.url)
                    this.previousUrl = ev.url

                    this.pageEnterTime = 0
                    posthog.capture('$pageleave')
                }
            })
        )
    }

    private loadFeatureFlags(flags: string[]) {
        try {
            const flagStates: Record<string, boolean> = {}
            const multiVariantFlags: Record<string, string> = {}

            flags.forEach((flag) => {
                const flagValue = posthog.getFeatureFlag(flag)
                if (!flagValue) {
                    flagStates[flag] = false
                    multiVariantFlags[flag] = ''
                } else if (typeof flagValue === 'string') {
                    multiVariantFlags[flag] = flagValue
                } else if (typeof flagValue === 'boolean') {
                    flagStates[flag] = Boolean(flagValue)
                }
            })
            this.featureFlags.next(flagStates)
            this.multiVariantFlags.next(multiVariantFlags)
            this.flagsLoaded$.next(true)
        } catch (err) {
            this.parseService.reportUIError(err, 'PosthogService.loadFeatureFlags', '')
            this.flagsLoaded$.next(false)
        }
    }

    public flagsLoaded(): BehaviorSubject<boolean> {
        return this.flagsLoaded$
    }

    private handleIdentifyUser() {
        let hasBeenLoggedIn = false
        this.subscriptions.add(
            this.parseService.user.subscribe((loggedInUser) => {
                try {
                    if (loggedInUser) {
                        hasBeenLoggedIn = true
                        this.identifyUser(loggedInUser.id)
                    } else if (!loggedInUser && hasBeenLoggedIn) {
                        this.resetUser()
                    }
                } catch (err) {
                    this.parseService.reportUIError(err, 'PosthogService.handleIdentifyUser.User.Subscribe', '')
                }
            })
        )
    }

    async isFeatureEnabled(flag: string): Promise<boolean> {
        if (!this.flagsLoaded$.value || !this.featureFlags.value[flag]) {
            return new Promise((resolve) => {
                this.subscriptions.add(
                    this.flagsLoaded$
                        .pipe(
                            filter((flagsLoaded) => flagsLoaded),
                            take(1),
                            timeout({
                                first: 5000,
                                with: () => of(false),
                            })
                        )
                        .subscribe({
                            next: (flagsLoaded) => {
                                resolve(flagsLoaded ? (this.featureFlags.value?.[flag] ?? false) : false)
                            },
                            error: () => {
                                resolve(false)
                            },
                        })
                )
            })
        }
        return !!this.featureFlags.value[flag]
    }

    async getFeatureFlagVariant(flag: string): Promise<string | null> {
        if (!this.flagsLoaded$.value || !this.multiVariantFlags.value[flag]) {
            return new Promise((resolve) => {
                this.subscriptions.add(
                    this.flagsLoaded$
                        .pipe(
                            filter((flagsLoaded) => flagsLoaded),
                            take(1),
                            timeout({
                                first: 5000,
                                with: () => of(null),
                            })
                        )
                        .subscribe({
                            next: (flagsLoaded) => {
                                resolve(flagsLoaded ? (this.multiVariantFlags.value?.[flag] ?? null) : null)
                            },
                            error: () => {
                                resolve(null)
                            },
                        })
                )
            })
        }
        return this.multiVariantFlags.value[flag]
    }

    private identifyUser(id: string) {
        try {
            if (!posthog._isIdentified()) {
                posthog.identify(id, { id })
            }
        } catch (err) {
            this.parseService.reportUIError(err, 'PosthogService.identifyUser', '')
        }
    }

    private resetUser() {
        try {
            posthog.reset()
        } catch (err) {
            this.parseService.reportUIError(err, 'PosthogService.resetUser', '')
        }
    }

    ngOnDestroy(): void {
        if (this.subscriptions) {
            this.subscriptions.unsubscribe()
        }
        this.disableSessionRecording()
    }

    private initPostHog() {
        try {
            const isInitialized = this.environmentService.POSTHOG_PROJECT_KEY && !this.isInitialized$.value
            const posthogKey = this.environmentService.POSTHOG_PROJECT_KEY || ''
            // It is safe to assume POSTHOG_PROJECT_KEY will be available in all environments & this.isInitialized$.next(true) will be called as a result
            if (isInitialized) {
                this.ngZone.runOutsideAngular(() => {
                    posthog.init(posthogKey, {
                        api_host: 'https://us.i.posthog.com',
                        person_profiles: 'identified_only',
                        capture_pageview: false,
                        capture_pageleave: false,
                        disable_session_recording: true, // default session recording is disabled
                        session_recording: {
                            maskAllInputs: false,
                            recordCrossOriginIframes: false,
                        },
                        // TODO(WSP-1167) This was not tested and evaluated as fixing issue
                        // autocapture: {
                        //     dom_event_allowlist: ['click', 'submit'],
                        //     element_allowlist: ['a', 'button', 'input', 'select', 'textarea'],
                        // },
                    })
                })
                this.isInitialized$.next(true)
            }
        } catch (err) {
            this.parseService.reportUIError(err, 'PosthogService.initPostHog', '')
        }
    }

    private disableSessionRecording() {
        if (!this.isInitialized$.value) {
            return
        }

        if (this.isRecordingSession && posthog.sessionRecordingStarted()) {
            posthog.stopSessionRecording()
            posthog.capture('$session_recording_stopped', {
                timestamp: Date.now(),
            })
            this.isRecordingSession = false
        }
    }

    private enableSessionRecording() {
        if (!this.isInitialized$.value) {
            return
        }

        if (!this.isRecordingSession && !posthog.sessionRecordingStarted()) {
            posthog.startSessionRecording()
            this.isRecordingSession = true
            posthog.capture('$session_recording_started', {
                timestamp: Date.now(),
            })
        }
    }

    private handleSessionRecording(url: string): void {
        if (!this.isInitialized$.value) {
            return
        }

        const shouldRecord = this.recordingUrls.some((pattern) => pattern.test(url))

        if (shouldRecord) {
            this.enableSessionRecording()
        } else {
            this.disableSessionRecording()
        }
    }

    private calculateTimeSpentBeforeAnEvent(): number {
        if (this.pageEnterTime === 0) return 0
        const currentTime = Date.now()
        return Math.max(0, Math.round((currentTime - this.pageEnterTime) / 1000))
    }

    public trackTime(): number {
        return this.calculateTimeSpentBeforeAnEvent()
    }

    private trackPageDropoff(fromUrl: string, toUrl: string) {
        const time = this.trackTime()
        if (fromUrl && fromUrl !== toUrl) {
            posthog.capture('$pageDropoff', {
                fromUrl: fromUrl,
                toUrl: toUrl,
                timeSpent: time,
            })
        }
    }

    public capture(eventName: PosthogTracking.Events.AdvancedEditorLoadCompleted, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.AdvancedEditorLoadFailed, properties: EmptyObject): void
    public capture(
        eventName: PosthogTracking.Events.ProductSelected,
        properties: { property: Objects.Product.Type.Sign | Objects.Product.Type.BusinessCard | Objects.Product.Code.Any | undefined }
    ): void
    public capture(eventName: PosthogTracking.Events.TemplateViewed, properties: { property: string }): void
    public capture(eventName: PosthogTracking.Events.TemplateProductFilters, properties: { [property: string]: string }): void
    public capture(eventName: PosthogTracking.Events.TemplateSearch, properties: { search_query: string }): void
    public capture(eventName: PosthogTracking.Events.ExpressEditorProofDownloaded, properties: { time_spent: number }): void
    public capture(eventName: PosthogTracking.Events.ExpressEditorEmailProofSent, properties: { time_spent: number }): void
    public capture(eventName: PosthogTracking.Events.ExpressEditorSavedAsNewTemplate, properties: { time_spent: number }): void
    public capture(eventName: PosthogTracking.Events.ExpressEditorNextClicked, properties: { time_spent: number }): void
    public capture(eventName: PosthogTracking.Events.ExpressEditorSavedChanges, properties: { time_spent: number }): void
    public capture(eventName: PosthogTracking.Events.AdvancedEditorClicked, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.AdvancedEditorPreviewDownloaded, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.AdvancedEditorSaved, properties: { time_spent: number }): void
    public capture(eventName: PosthogTracking.Events.AdvancedEditorSavedAndExited, properties: { time_spent: number }): void
    public capture(eventName: PosthogTracking.Events.AdvancedEditorExited, properties: { time_spent: number }): void
    public capture(eventName: PosthogTracking.Events.MailingListProSubscriptionLinkClicked, properties: { searched_by: SearchBy; time_spent: number }): void
    public capture(eventName: PosthogTracking.Events.MailingListPurchased, properties: { time_spent: number }): void
    public capture(
        eventName: PosthogTracking.Events.MailingListOptionsSelected,
        properties: { search_type: string; record_limit: number; time_spent: number }
    ): void
    public capture(eventName: PosthogTracking.Events.MailingListProSubscriptionPurchased, properties: { subscription_type: string; time_spent: number }): void
    public capture(eventName: PosthogTracking.Events.BillingSubscriptionSliderSlid, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.BillingSubscriptionFiveCentsAdded, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.BillingSubscriptionFiveCentsRemoved, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.BillingSubscriptionDroppedOff, properties: { step_name: string }): void
    public capture(eventName: PosthogTracking.Events.BillingSubscriptionPurchased, properties: { subscription_type: string; time_spent: number }): void
    public capture(eventName: PosthogTracking.Events.UploadListAutoUploaded, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.UploadListUpdateExistingList, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.UploadListAddedUnmappedFields, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.UploadListIgnoredUnmappedFields, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.UploadListDeleted, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.UploadListSaved, properties: { time_spent: number }): void
    public capture(eventName: PosthogTracking.Events.FreePostcardsCopiedReferralCode, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.FreePostcardsShowEmailTemplateClicked, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.SuppressionListAdded, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.SuppressionListRowAdded, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.SuppressionListReplaced, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.SuppressionListRowDeleted, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.OrderManagementCreateSimilarClicked, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.OrderSummaryPurchaseMailingListClicked, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.OrderSummarySaveChangesClicked, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.OrderSummaryCheckoutCompleted, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.OrderSummaryBackClicked, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.ManageMailingListPurchaseClicked, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.ManageMailingListAddMyAddressClicked, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.ManageMailingListAddAddressClicked, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.ManageMailingListRefreshClicked, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.ManageMailingListUploadClicked, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.ManageMailingListViewOriginalCSVClicked, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.ManageMailingListViewMappedCSVClicked, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.ManageMailingListRunDuplicateDetectionClicked, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.ManageMailingListRunAgainstSuppressionListClicked, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.ManageMailingListCreateFacebookAudienceClicked, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.NavbarSubscribeAndSaveClicked, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.UserPilotBannerClicked, properties: { currentPage: string; extras?: UnknownObject }): void
    public capture(eventName: PosthogTracking.Events.OnboardingLetsGetStartedClicked, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.OnboardingRealEstateAgentNextClicked, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.OnboardingSuccessPageUpgradeNowClicked, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.OnboardingSuccessPageUpgradeLaterClicked, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.OnboardingConnectMlsUser, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.SkipOnboardingConnectMlsUser, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.OnboardingGetPostcardSampleClicked, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.OnboardingSkipPostcardSampleClicked, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.OnboardingDroppedOff, properties: { step_name: string }): void
    public capture(eventName: PosthogTracking.Events.OnboardingConfirmDesignProfileClicked, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.OnboardingConfirmDesignProfileBackButtonClicked, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.MegaMenuShopSubscriptionNowClicked, properties: EmptyObject): void
    public capture(eventName: PosthogTracking.Events.NavbarCreateNewOrderClicked, properties: { btn_location: string }): void
    public capture(eventName: PosthogTracking.Events.TemplatePageLeftPanelProductSelected, properties: { product: Objects.Product.Code.Any }): void
    public capture(eventName: PosthogTracking.Events.TemplatesFiltersSelected, properties: { filters: Record<string, string>; isSeeAll: boolean }): void
    public capture(eventName: string, properties: object): void {
        try {
            if (!this.isInitialized$.value) {
                return
            }
            posthog.capture(eventName, properties)
        } catch (err) {}
    }
}
